import { ApiError, handleApiError } from '@/lib/api';
import { getAccessToken } from '@privy-io/react-auth';
import type { MediaUploadResponse, MediaUploadOptions } from './types';
import { DEFAULT_UPLOAD_OPTIONS } from './types';

export const mediaApi = {
  uploadFile: async (
    file: File,
    options: MediaUploadOptions = {}
  ): Promise<MediaUploadResponse> => {
    const {
      folder = DEFAULT_UPLOAD_OPTIONS.folder!,
      maxSize = DEFAULT_UPLOAD_OPTIONS.maxSize!,
      allowedTypes = DEFAULT_UPLOAD_OPTIONS.allowedTypes!,
    } = options;

    try {
      // Validate file size
      if (file.size > maxSize) {
        throw new ApiError(
          400,
          'Bad Request',
          `File size must be less than ${maxSize / (1024 * 1024)}MB`
        );
      }

      // Validate file type
      if (!allowedTypes.includes(file.type)) {
        throw new ApiError(
          400,
          'Bad Request',
          `Only ${allowedTypes.join(', ')} files are allowed`
        );
      }

      const formData = new FormData();
      formData.append('file', file);

      // Get token for authenticated upload
      const token = await getAccessToken();

      // Use the API base URL from config with folder as query parameter
      const url = new URL(
        `${import.meta.env.VITE_API_BASE_URL}/admin/media/upload`
      );
      url.searchParams.append('folder', folder);

      const response = await fetch(url.toString(), {
        method: 'POST',
        body: formData,
        headers: {
          // Let browser set Content-Type with boundary for multipart/form-data
          'privy-id-token': token || '',
        },
      });

      if (!response.ok) {
        let errorMessage = 'Upload failed';
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorData.error || errorMessage;
        } catch {
          errorMessage = await response.text().catch(() => errorMessage);
        }

        throw new ApiError(response.status, response.statusText, errorMessage);
      }

      return await response.json();
    } catch (error) {
      handleApiError(error, {
        operation: 'upload_media',
        filename: file.name,
        filesize: file.size,
        filetype: file.type,
        folder,
      });
      throw error;
    }
  },
};
